import React, { useEffect, useState } from 'react';
import { IconChevronLeft, IconChevronRight } from '@tabler/icons-react';
import { Box, Button, Title } from '@mantine/core';
import { theme } from '@/theme';
import { useScrollable } from '@/components/common/hooks/useScrollable';
import SearchIconWithPlus from '@/components/common/shared/SearchIconWithPlus/SearchIconWithPlus';
import MonthlyChart from '@/components/common/shared/Charts/charts/MonthlyChart';
import QuarterlyChart from '@/components/common/shared/Charts/charts/QuarterlyChart';
import SpotMonthlyChart from '@/components/common/shared/Charts/charts/SpotMonthlyChart';
import YearlyChart from '@/components/common/shared/Charts/charts/YearlyChart';
import CustomModal from '@/components/common/shared/Modal/CustomModal';
import styles from './ChartWrapper.module.css';

interface ChartsContainerProps {
  categoryData: {
    spot?: string[];
    monthly?: string[];
    quarterly?: string[];
    yearly?: string[];
    defaultMonthly?: string;
    defaultQuarterly?: string;
    defaultYearly?: string;
  };
}

const ChartWrapperDashboard: React.FC<ChartsContainerProps> = ({ categoryData }) => {
  const {
    spot = [],
    monthly = [],
    quarterly = [],
    yearly = [],
    defaultMonthly,
    defaultQuarterly,
    defaultYearly,
  } = categoryData || {};

  const [modalOpened, setModalOpened] = useState(false);
  const [modalContent, setModalContent] = useState<React.ReactNode>(null);

  const { scrollRef, scrollLeft, scrollRight, canScrollLeft, canScrollRight, updateScrollState } =
    useScrollable();

  useEffect(() => {
    updateScrollState();
  }, [spot, monthly, quarterly, yearly]);

  const handleChartClick = (title: string, chart: React.ReactElement) => {
    setModalContent(
      <div>
        <Title order={4}>{title}</Title>
        {React.cloneElement(chart, { isModalView: true })}
      </div>
    );
    setModalOpened(true);
  };

  const renderChartSection = (title: string, children: React.ReactElement) => (
    <div
      className={styles.chartGridCol}
      onClick={() => handleChartClick(title, children)}
      style={{ cursor: 'pointer' }}
    >
      <Box className={styles.titleAbove}>
        <Title order={4}>{title}</Title>
      </Box>
      <div className={styles.paper}>
        {React.cloneElement(children, { isModalView: false })}
        <div className={styles.iconContainer}>
          <SearchIconWithPlus size={48} color={theme.colors.baseOrange[6]} />
        </div>
        </div>
    </div>
  );

  return (
    <>
      <div className={styles.scrollContainer}>
        <Button
          className={`${styles.scrollButton} ${canScrollLeft ? styles.scrollButtonClickable : ''}`}
          onClick={scrollLeft}
          variant="subtle"
          disabled={!canScrollLeft}
        >
          <IconChevronLeft size={24} />
        </Button>
        <div ref={scrollRef} className={styles.horizontalScroll}>
          {spot.map((spotContract, index) =>
            renderChartSection(
              `${spotContract}`,
              <SpotMonthlyChart key={index} contract={spotContract} />
            )
          )}
          {monthly.length > 0 &&
            renderChartSection(
              'Miesięczny',
              <MonthlyChart contracts={monthly} defaultContract={defaultMonthly} />
            )}
          {quarterly.some((q) => q) &&
            renderChartSection(
              'Kwartalny',
              <QuarterlyChart contracts={quarterly} defaultContract={defaultQuarterly} />
            )}
          {yearly.some((y) => y) &&
            renderChartSection(
              'Roczny',
              <YearlyChart contracts={yearly} defaultContract={defaultYearly} />
            )}
        </div>
        <Button
          className={`${styles.scrollButton} ${canScrollRight ? styles.scrollButtonClickable : ''}`}
          onClick={scrollRight}
          variant="subtle"
          disabled={!canScrollRight}
        >
          <IconChevronRight size={24} />
        </Button>
      </div>
      <CustomModal
        opened={modalOpened}
        onClose={() => setModalOpened(false)}
        showButtons={false}
        size="100%"
      >
        {modalContent}
      </CustomModal>
    </>
  );
};

export default ChartWrapperDashboard;
